import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../context/AuthContext";
import { Plus, Pencil, Trash2, Save, X, RefreshCw, ExternalLink, Users } from "lucide-react";

const empty = {
  title: "",
  description: "",
  start_datetime: "",
  end_datetime: "",
  venue: "",
  address: "",
  city: "",
  ticket_url: "",
};

function fmtLocal(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminFestivals() {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState(null);
  const [rsvpSummary, setRsvpSummary] = useState({});
  const [viewingRsvps, setViewingRsvps] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/admin/festivals`, { withCredentials: true }),
        axios.get(`${API}/admin/rsvps/summary`, { withCredentials: true }),
      ]);
      setEvents(r.data || []);
      setRsvpSummary(s.data || {});
    } catch {
      setError("Could not load festival events.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const now = Date.now();
  const { upcoming, past } = useMemo(() => {
    const u = []; const p = [];
    events.forEach((e) => {
      if (new Date(e.start_datetime).getTime() >= now) u.push(e); else p.push(e);
    });
    return { upcoming: u, past: p };
  }, [events, now]);

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await axios.delete(`${API}/admin/festivals/${id}`, { withCredentials: true }); load(); }
    catch { alert("Action failed."); }
  };

  return (
    <div data-testid="admin-tab-festivals">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-black text-[#0A0A0A] text-2xl tracking-tight">Festival dates</h2>
          <p className="text-sm text-[#2C2C2C]">Add, edit and remove upcoming screenings and events.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy} data-testid="admin-festivals-refresh" className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <button onClick={() => setShowAdd(true)} data-testid="admin-festivals-add" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add event</button>
        </div>
      </div>

      {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 mb-4" role="alert">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Stat label="Total" value={events.length} bg="bg-[#FFD93D]" />
        <Stat label="Upcoming" value={upcoming.length} bg="bg-[#1A5DFF]" text="text-[#FFF8E7]" />
        <Stat label="Past" value={past.length} bg="bg-white" />
      </div>

      <h3 className="font-heading font-bold text-[#0A0A0A] text-lg mb-3">Upcoming</h3>
      <div className="space-y-3 mb-10" data-testid="admin-festivals-upcoming">
        {upcoming.map((e) => <Row key={e.id} ev={e} rsvp={rsvpSummary[e.id]} onViewRsvps={() => setViewingRsvps(e)} onEdit={() => setEditing(e)} onDelete={() => del(e.id, e.title)} />)}
        {!busy && upcoming.length === 0 && <div className="text-[#2C2C2C]">No upcoming events.</div>}
      </div>

      {past.length > 0 && (
        <>
          <h3 className="font-heading font-bold text-[#0A0A0A] text-lg mb-3">Past</h3>
          <div className="space-y-3 opacity-80" data-testid="admin-festivals-past">
            {past.map((e) => <Row key={e.id} ev={e} rsvp={rsvpSummary[e.id]} onViewRsvps={() => setViewingRsvps(e)} onEdit={() => setEditing(e)} onDelete={() => del(e.id, e.title)} />)}
          </div>
        </>
      )}

      {showAdd && <FormModal title="Add festival event" initial={empty} mode="create" onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <FormModal title="Edit festival event" initial={editing} mode="edit" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewingRsvps && <RsvpListModal event={viewingRsvps} onClose={() => setViewingRsvps(null)} />}
    </div>
  );
}

function Stat({ label, value, bg, text = "text-[#0A0A0A]" }) {
  return (
    <div className={`${bg} ${text} border-2 border-[#0A0A0A] p-4`}>
      <div className="text-xs uppercase tracking-[0.18em] font-bold">{label}</div>
      <div className="font-heading font-black text-3xl mt-1">{value}</div>
    </div>
  );
}

function Row({ ev, rsvp, onViewRsvps, onEdit, onDelete }) {
  return (
    <div className="bg-white border-2 border-[#0A0A0A] p-4 md:p-5 flex flex-col md:flex-row gap-4 items-start" data-testid={`admin-festival-${ev.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#1A5DFF] mb-1">{fmtLocal(ev.start_datetime)}{ev.end_datetime && ` → ${fmtLocal(ev.end_datetime)}`}</div>
        <h4 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{ev.title}</h4>
        <div className="text-sm text-[#2C2C2C] mt-1"><span className="font-bold text-[#0A0A0A]">{ev.venue}</span> · {ev.address} · {ev.city}</div>
        <p className="text-sm text-[#2C2C2C] mt-2 line-clamp-2">{ev.description}</p>
        {ev.ticket_url && (
          <a href={ev.ticket_url} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-[#FF2E63] underline inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> {ev.ticket_url}
          </a>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onViewRsvps} data-testid={`admin-festival-rsvps-${ev.id}`} className="bg-[#1A5DFF] text-[#FFF8E7] hover:bg-[#0E4ACC] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> {rsvp ? `${rsvp.count} RSVP${rsvp.count === 1 ? "" : "s"} · ${rsvp.seats} seats` : "0 RSVPs"}
        </button>
        <button onClick={onEdit} data-testid={`admin-festival-edit-${ev.id}`} className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
        <button onClick={onDelete} data-testid={`admin-festival-delete-${ev.id}`} className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] rounded-none px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
      </div>
    </div>
  );
}

function RsvpListModal({ event, onClose }) {
  const [rsvps, setRsvps] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/festivals/${event.id}/rsvps`, { withCredentials: true });
        setRsvps(r.data || []);
      } catch { setRsvps([]); }
    })();
  }, [event.id]);

  const totalSeats = (rsvps || []).reduce((sum, r) => sum + (r.seats || 1), 0);

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="rsvp-list-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-2xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">Registered interest</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl">{event.title}</h2>
            {rsvps && <p className="text-sm text-[#2C2C2C] mt-1">{rsvps.length} {rsvps.length === 1 ? "person" : "people"} · {totalSeats} seats requested</p>}
          </div>
          <button type="button" onClick={onClose} data-testid="rsvp-list-close" className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>
        {!rsvps && <div className="text-[#2C2C2C]">Loading…</div>}
        {rsvps && rsvps.length === 0 && <div className="text-[#2C2C2C]" data-testid="rsvp-list-empty">No one has registered interest yet.</div>}
        {rsvps && rsvps.length > 0 && (
          <div className="border-2 border-[#0A0A0A] bg-white overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A] bg-[#FFE8A8] text-xs uppercase tracking-[0.14em]">
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Seats</th>
                  <th className="px-4 py-2">Registered</th>
                </tr>
              </thead>
              <tbody>
                {rsvps.map((r) => (
                  <tr key={r.id} className="border-b border-[#0A0A0A]/20" data-testid={`rsvp-row-${r.id}`}>
                    <td className="px-4 py-2 font-bold">{r.name}</td>
                    <td className="px-4 py-2"><a href={`mailto:${r.email}`} className="underline">{r.email}</a></td>
                    <td className="px-4 py-2">{r.seats}</td>
                    <td className="px-4 py-2 text-[#2C2C2C] whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FormModal({ title, initial, mode, onClose, onSaved }) {
  // Convert ISO to datetime-local-friendly value if needed: keep first 16 chars
  const trim = (s) => (s ? String(s).slice(0, 16) : "");
  const [form, setForm] = useState({
    title: initial.title || "",
    description: initial.description || "",
    start_datetime: trim(initial.start_datetime),
    end_datetime: trim(initial.end_datetime || ""),
    venue: initial.venue || "",
    address: initial.address || "",
    city: initial.city || "",
    ticket_url: initial.ticket_url || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      const payload = { ...form, end_datetime: form.end_datetime || null, ticket_url: form.ticket_url || null };
      if (mode === "create") await axios.post(`${API}/admin/festivals`, payload, { withCredentials: true });
      else await axios.put(`${API}/admin/festivals/${initial.id}`, payload, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="festival-form-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-3xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">{mode === "create" ? "New event" : "Edit"}</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl">{title}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="festival-form-close" className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Event title" required full><input data-testid="admin-fest-title" value={form.title} onChange={(e) => set("title", e.target.value)} required className={cls} /></F>
          <F label="Start (date & time)" required hint="Your local time"><input data-testid="admin-fest-start" type="datetime-local" value={form.start_datetime} onChange={(e) => set("start_datetime", e.target.value)} required className={cls} /></F>
          <F label="End (optional)"><input data-testid="admin-fest-end" type="datetime-local" value={form.end_datetime} onChange={(e) => set("end_datetime", e.target.value)} className={cls} /></F>
          <F label="Venue" required><input data-testid="admin-fest-venue" value={form.venue} onChange={(e) => set("venue", e.target.value)} required className={cls} /></F>
          <F label="City" required hint="e.g. Manchester, UK"><input data-testid="admin-fest-city" value={form.city} onChange={(e) => set("city", e.target.value)} required className={cls} /></F>
          <F label="Address" required full><input data-testid="admin-fest-address" value={form.address} onChange={(e) => set("address", e.target.value)} required className={cls} /></F>
          <F label="Ticket URL" hint="Leave blank if free / drop-in" full><input data-testid="admin-fest-ticket" type="url" value={form.ticket_url} onChange={(e) => set("ticket_url", e.target.value)} placeholder="https://" className={cls} /></F>
          <F label="Description" required full><textarea data-testid="admin-fest-desc" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} required className={cls} /></F>
        </div>

        {error && <div className="mt-4 bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm">{error}</div>}

        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-2.5 text-sm font-medium">Cancel</button>
          <button type="submit" disabled={submitting} data-testid="admin-save-festival" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save className="w-4 h-4" /> {submitting ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

const cls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none text-sm";

function F({ label, hint, children, required, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">{label} {required && <span className="text-[#FF2E63]">*</span>}</span>
      {children}
      {hint && <span className="text-xs text-[#2C2C2C]">{hint}</span>}
    </label>
  );
}
